import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const todayIso = () => iso(new Date());

export default function MonthGrid({ data, loading, cursor, setCursor, headerRight, renderEvent, onDayClick, selectedRange, maxPerDay = 3 }) {
  const weeks = useMemo(() => {
    if (!data) return [];
    let cur = parseIso(data.start);
    const end = parseIso(data.end);
    const out = [];
    while (cur <= end) {
      const row = [];
      for (let i = 0; i < 7; i++) { row.push(iso(cur)); cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1); }
      out.push(row);
    }
    return out;
  }, [data]);

  const inMonth = (d) => data && d >= data.in_month[0] && d <= data.in_month[1];
  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const inRange = (d) => selectedRange?.from && d >= selectedRange.from && d <= (selectedRange.to || selectedRange.from);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading font-bold text-lg capitalize tracking-tight">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
        <div className="flex items-center gap-2">
          {headerRight}
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })} data-testid="cal-today">Hoy</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="h-[520px] rounded-xl bg-muted/40 animate-pulse" />
      ) : (
        <Card className="overflow-hidden border-border shadow-none">
          <div className="grid grid-cols-7 border-b border-border bg-muted/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {DOW.map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div>
            {weeks.map((row, ri) => (
              <div key={ri} className="grid grid-cols-7 border-b border-border last:border-b-0">
                {row.map((d) => {
                  const events = (data.days || {})[d] || [];
                  const isToday = d === todayIso();
                  const dim = !inMonth(d);
                  const visible = events.slice(0, maxPerDay);
                  const extra = events.length - visible.length;
                  return (
                    <div
                      key={d}
                      onClick={onDayClick ? () => onDayClick(d) : undefined}
                      className={cn(
                        "min-h-[104px] border-r border-border p-1.5 last:border-r-0 transition-colors duration-150",
                        dim ? "bg-muted/20" : "bg-card",
                        onDayClick && "cursor-pointer hover:bg-muted/40",
                        inRange(d) && "bg-primary/10 hover:bg-primary/15",
                      )}
                      data-testid={`cal-day-${d}`}
                    >
                      <div className="mb-1 flex justify-end">
                        <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-xs",
                          isToday ? "bg-primary font-bold text-primary-foreground" : dim ? "text-muted-foreground/50" : "text-muted-foreground")}>
                          {parseIso(d).getDate()}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {visible.map((e, i) => renderEvent(e, i))}
                        {extra > 0 && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button onClick={(ev) => ev.stopPropagation()} className="w-full rounded px-1.5 py-0.5 text-left text-[11px] font-medium text-muted-foreground hover:bg-muted" data-testid={`cal-more-${d}`}>+{extra} más</button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="start">
                              <p className="px-1 pb-1.5 text-xs font-semibold capitalize">{parseIso(d).getDate()} {MONTHS[parseIso(d).getMonth()]}</p>
                              <div className="space-y-1">{events.map((e, i) => renderEvent(e, i))}</div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
